/**
 * HeroGlobe.js
 * One lightweight animated hero background for every screen size — replaces
 * the old HeroBackground (desktop) + HeroBackgroundMobile (phones) pair.
 *
 * Built for low-end Android on metered mobile data (Southern Africa):
 *   • Pure inline SVG — zero network requests, no images, no fonts, no video.
 *   • NO SVG <filter> / feGaussianBlur (the single biggest cause of jank on
 *     cheap GPUs) — every glow is a plain radialGradient instead.
 *   • Motion is opacity/transform only, plus native SMIL <animateMotion> for
 *     the planes (declarative, no JS, no reflow, GPU-cheap).
 *   • ~35 nodes total; prefers-reduced-motion freezes all of it.
 *
 * The picture: a wireframe globe with great-circle routes arcing between
 * continents and small planes tracing them — "we move people around the
 * world" — on the same royal-blue gradient as before.
 */
import React from 'react';
import './HeroGlobe.css';

// ── Globe geometry (viewBox 1440 x 900) ──────────────────────────────────
const GX = 900;
const GY = 452;
const GR = 300;

// Latitude rings — ellipses squashed for a slight perspective tilt.
const LAT = [-232, -150, -74, 0, 74, 150, 232].map((dy) => {
  const k = dy / GR;
  const rx = Math.round(GR * Math.sqrt(Math.max(0, 1 - k * k)));
  return { cy: GY + dy, rx, ry: Math.max(2, Math.round(rx * 0.16)) };
});

// Longitude rings — every 30°, drawn as ellipses narrowing toward the poles.
const LON = [18, 52, 90, 128, 162].map((deg) => ({
  rx: Math.round(GR * Math.sin((deg * Math.PI) / 180)),
  ry: GR,
}));

// Great-circle-ish routes. Endpoints sit near the globe so they read as
// cities; the control point bows each line outward into an arc. One leg is
// deliberately anchored low-left (Southern Africa) reaching up and out.
const ROUTES = [
  { id: 'r1', d: `M ${GX - 150} ${GY + 205} Q ${GX - 40} ${GY - 60} ${GX + 170} ${GY - 150}`, dur: 9 },
  { id: 'r2', d: `M ${GX - 236} ${GY + 40} Q ${GX + 20} ${GY - 240} ${GX + 250} ${GY - 40}`, dur: 11 },
  { id: 'r3', d: `M ${GX - 90} ${GY + 250} Q ${GX + 140} ${GY + 150} ${GX + 250} ${GY + 110}`, dur: 10 },
  { id: 'r4', d: `M ${GX + 40} ${GY - 235} Q ${GX + 260} ${GY - 60} ${GX + 210} ${GY + 175}`, dur: 12.5 },
];

// City dots = the route endpoints worth marking.
const CITIES = [
  [GX - 150, GY + 205],
  [GX + 170, GY - 150],
  [GX - 236, GY + 40],
  [GX + 250, GY - 40],
  [GX + 250, GY + 110],
  [GX + 40, GY - 235],
];

const STARS = [
  [120, 90], [300, 190], [520, 70], [180, 380], [90, 560],
  [1360, 140], [1300, 420], [1380, 680], [1180, 90], [420, 640],
];

const Plane = ({ href, dur, begin }) => (
  // A small chevron; rotate="auto" banks it along the arc. Both href and
  // xlinkHref on <mpath> — some mobile WebViews only honour the xlink form.
  <g className="hg-plane">
    <path d="M 0 -5 L 12 0 L 0 5 L 3 0 Z" fill="rgba(220,235,255,0.95)" />
    <animateMotion
      dur={`${dur}s`}
      begin={`${begin}s`}
      repeatCount="indefinite"
      rotate="auto"
    >
      <mpath href={`#${href}`} xlinkHref={`#${href}`} />
    </animateMotion>
  </g>
);

export default function HeroGlobe() {
  return (
    <div className="hg-root" aria-hidden="true">
      <svg
        className="hg-svg"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="hgBg" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0a1a6e" />
            <stop offset="32%" stopColor="#011ff3" />
            <stop offset="70%" stopColor="#010e6e" />
            <stop offset="100%" stopColor="#00052a" />
          </linearGradient>

          {/* Glow behind the globe — a gradient, not a blur filter. */}
          <radialGradient id="hgGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#4f8fff" stopOpacity="0.28" />
            <stop offset="55%" stopColor="#1a3aff" stopOpacity="0.09" />
            <stop offset="100%" stopColor="#011ff3" stopOpacity="0" />
          </radialGradient>

          {/* Darker left edge so headline text keeps contrast. */}
          <linearGradient id="hgLeft" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#00031a" stopOpacity="0.6" />
            <stop offset="48%" stopColor="#00031a" stopOpacity="0" />
          </linearGradient>

          <radialGradient id="hgSurface" cx="38%" cy="34%" r="66%">
            <stop offset="0%" stopColor="#5b9fff" stopOpacity="0.14" />
            <stop offset="100%" stopColor="#011ff3" stopOpacity="0" />
          </radialGradient>

          <clipPath id="hgClip">
            <circle cx={GX} cy={GY} r={GR} />
          </clipPath>
        </defs>

        {/* Background */}
        <rect width="1440" height="900" fill="url(#hgBg)" />
        <rect
          x={GX - GR * 1.7}
          y={GY - GR * 1.7}
          width={GR * 3.4}
          height={GR * 3.4}
          fill="url(#hgGlow)"
        />

        {/* Stars (mostly static; two slow twinklers) */}
        {STARS.map(([x, y], i) => (
          <circle
            key={`s${i}`}
            cx={x}
            cy={y}
            r="1.3"
            fill="rgba(200,220,255,0.5)"
            className={i % 5 === 0 ? 'hg-star' : undefined}
            style={i % 5 === 0 ? { animationDelay: `${i}s` } : undefined}
          />
        ))}

        {/* Globe */}
        <circle cx={GX} cy={GY} r={GR} fill="url(#hgSurface)" />
        <g clipPath="url(#hgClip)" stroke="rgba(160,200,255,0.22)" strokeWidth="1" fill="none">
          {LAT.map(({ cy, rx, ry }, i) => (
            <ellipse key={`la${i}`} cx={GX} cy={cy} rx={rx} ry={ry} />
          ))}
          {LON.map(({ rx, ry }, i) => (
            <ellipse key={`lo${i}`} cx={GX} cy={GY} rx={rx} ry={ry} />
          ))}
        </g>
        <circle
          cx={GX}
          cy={GY}
          r={GR}
          fill="none"
          stroke="rgba(120,180,255,0.4)"
          strokeWidth="1.5"
          className="hg-ring"
        />

        {/* Routes — a dim static arc with a bright pulse running along it.
            pathLength="1000" normalises every arc, so the same 46-unit dash
            reads as a short lit segment on all four. The dash is driven by a
            native SMIL <animate> (not a CSS animation) for the same reason
            the planes are: it keeps moving in environments that pause the
            CSS/Web-Animations clock, and needs no JS. */}
        <g fill="none" strokeLinecap="round">
          {ROUTES.map((r, i) => (
            <React.Fragment key={r.id}>
              <path
                id={r.id}
                d={r.d}
                pathLength="1000"
                stroke="rgba(130,195,255,0.32)"
                strokeWidth="1.2"
              />
              <path
                d={r.d}
                pathLength="1000"
                className="hg-flow"
                stroke="rgba(190,228,255,0.95)"
                strokeWidth="1.7"
                strokeDasharray="46 954"
                strokeDashoffset="1000"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="1000"
                  to="0"
                  dur={`${(r.dur * 0.55).toFixed(2)}s`}
                  begin={`${(-i * 1.6).toFixed(1)}s`}
                  repeatCount="indefinite"
                />
              </path>
            </React.Fragment>
          ))}
        </g>

        {/* City dots */}
        {CITIES.map(([x, y], i) => (
          <g key={`c${i}`} className="hg-city" style={{ animationDelay: `${i * 0.6}s` }}>
            <circle cx={x} cy={y} r="6" fill="rgba(120,190,255,0.18)" />
            <circle cx={x} cy={y} r="2.6" fill="rgba(190,225,255,0.95)" />
          </g>
        ))}

        {/* Planes tracing the routes */}
        <Plane href="r1" dur={ROUTES[0].dur} begin={0} />
        <Plane href="r2" dur={ROUTES[1].dur} begin={2.5} />
        <Plane href="r3" dur={ROUTES[2].dur} begin={5} />

        {/* Left-edge contrast wash, painted last so it sits over everything */}
        <rect width="1440" height="900" fill="url(#hgLeft)" />
      </svg>
    </div>
  );
}
